<template>
    <div>
        <div class="text-xl font-semibold">Text and background colors</div>
        <div class="mb-16 grid grid-flow-row grid-cols-3 gap-2">
            <div
                v-for="textColorClass in textColorClasses"
                :key="textColorClass"
                class="flex flex-col"
            >
                <div class="flex flex-row gap-2">
                    <div
                        class="rounded p-2"
                        :class="[textColorClass, 'bg-primary']"
                    >
                        Veni
                    </div>
                    <div
                        class="rounded p-2"
                        :class="[textColorClass, 'bg-secondary']"
                    >
                        Vidi
                    </div>
                    <div
                        class="rounded p-2"
                        :class="[textColorClass, 'bg-ds-primary-900']"
                    >
                        Vici
                    </div>
                </div>
                <code>{{ textColorClass }}</code>
            </div>
        </div>
        <div class="text-xl font-semibold">Semantic colors</div>
        <div
            v-for="semanticColor in semanticColors"
            :key="semanticColor.name"
        >
            <div class="text-xl font-semibold">{{ semanticColor.name }}</div>
            <div class="grid grid-flow-row grid-cols-10 gap-2">
                <div
                    v-for="stateClass in semanticColor.stateClasses"
                    :key="stateClass"
                >
                    <div
                        class="h-8 rounded-lg p-2"
                        :class="stateClass"
                    ></div>

                    <code>{{ getClassVariant(stateClass) }}</code>
                </div>
            </div>
        </div>

        <div class="mt-16 text-xl font-semibold">Color families</div>
        <div
            v-for="colorFamily in colorFamilies"
            :key="colorFamily.name"
        >
            <div class="text-xl font-semibold">{{ colorFamily.name }}</div>
            <div class="grid grid-flow-row grid-cols-10 gap-2">
                <div
                    v-for="swatchClass in colorFamily.swatchClasses"
                    :key="swatchClass"
                    class="p-2"
                >
                    <div
                        class="rounded-lg"
                        :class="[swatchClass, 'h-8 p-2']"
                    ></div>
                    <code>{{ getClassVariant(swatchClass) }}</code>
                </div>
            </div>
        </div>
        <div class="my-6 text-xl font-semibold">Divider</div>
        <div class="bg-divide h-3 w-full">
            <div class="grid w-1/2 grid-cols-3 gap-2 divide-x-8 divide-default">
                <div>01</div>
                <div>02</div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
/**
 * Composing names at runtime (`bg-${family}-${weight}`) is invisible to the scanner, so
 * every class below is written out in full so Tailwind generates it.
 */
const textColorClasses = [
    'text-content-primary',
    'text-content-secondary',
    'text-content-inverse',
    'text-content-error',
    'text-content-link',
    'text-content-disabled',
];

const semanticColors = [
    {
        name: 'primary',
        stateClasses: ['bg-semantic-primary-default', 'bg-semantic-primary-hover', 'bg-semantic-primary-pressed'],
    },
    {
        name: 'inverted',
        stateClasses: ['bg-semantic-inverted-default', 'bg-semantic-inverted-hover', 'bg-semantic-inverted-pressed'],
    },
    {
        name: 'secondary',
        stateClasses: ['bg-semantic-secondary-default', 'bg-semantic-secondary-hover', 'bg-semantic-secondary-pressed'],
    },
    {
        name: 'on-dark',
        stateClasses: ['bg-semantic-on-dark-default', 'bg-semantic-on-dark-hover', 'bg-semantic-on-dark-pressed'],
    },
    {
        name: 'destructive-primary',
        stateClasses: [
            'bg-semantic-destructive-primary-default',
            'bg-semantic-destructive-primary-hover',
            'bg-semantic-destructive-primary-pressed',
        ],
    },
    {
        name: 'destructive-secondary',
        stateClasses: [
            'bg-semantic-destructive-secondary-default',
            'bg-semantic-destructive-secondary-hover',
            'bg-semantic-destructive-secondary-pressed',
        ],
    },
];

const colorFamilies = [
    {
        name: 'ds-primary',
        swatchClasses: [
            'bg-ds-primary-50',
            'bg-ds-primary-100',
            'bg-ds-primary-200',
            'bg-ds-primary-300',
            'bg-ds-primary-400',
            'bg-ds-primary-500',
            'bg-ds-primary-600',
            'bg-ds-primary-700',
            'bg-ds-primary-800',
            'bg-ds-primary-900',
        ],
    },
    {
        name: 'ds-gray',
        swatchClasses: [
            'bg-ds-gray-50',
            'bg-ds-gray-100',
            'bg-ds-gray-200',
            'bg-ds-gray-300',
            'bg-ds-gray-400',
            'bg-ds-gray-500',
            'bg-ds-gray-600',
            'bg-ds-gray-700',
            'bg-ds-gray-800',
            'bg-ds-gray-900',
        ],
    },
    {
        name: 'ds-green',
        swatchClasses: [
            'bg-ds-green-50',
            'bg-ds-green-100',
            'bg-ds-green-200',
            'bg-ds-green-300',
            'bg-ds-green-400',
            'bg-ds-green-500',
            'bg-ds-green-600',
            'bg-ds-green-700',
            'bg-ds-green-800',
            'bg-ds-green-900',
        ],
    },
    {
        name: 'ds-blue',
        swatchClasses: [
            'bg-ds-blue-50',
            'bg-ds-blue-100',
            'bg-ds-blue-200',
            'bg-ds-blue-300',
            'bg-ds-blue-400',
            'bg-ds-blue-500',
            'bg-ds-blue-600',
            'bg-ds-blue-700',
            'bg-ds-blue-800',
            'bg-ds-blue-900',
        ],
    },
    {
        name: 'ds-cream',
        swatchClasses: [
            'bg-ds-cream-50',
            'bg-ds-cream-100',
            'bg-ds-cream-200',
            'bg-ds-cream-300',
            'bg-ds-cream-400',
            'bg-ds-cream-500',
            'bg-ds-cream-600',
            'bg-ds-cream-700',
            'bg-ds-cream-800',
            'bg-ds-cream-900',
        ],
    },
    {
        name: 'ds-orange',
        swatchClasses: [
            'bg-ds-orange-50',
            'bg-ds-orange-100',
            'bg-ds-orange-200',
            'bg-ds-orange-300',
            'bg-ds-orange-400',
            'bg-ds-orange-500',
            'bg-ds-orange-600',
            'bg-ds-orange-700',
            'bg-ds-orange-800',
            'bg-ds-orange-900',
        ],
    },
    {
        name: 'ds-yellow',
        swatchClasses: [
            'bg-ds-yellow-50',
            'bg-ds-yellow-100',
            'bg-ds-yellow-200',
            'bg-ds-yellow-300',
            'bg-ds-yellow-400',
            'bg-ds-yellow-500',
            'bg-ds-yellow-600',
            'bg-ds-yellow-700',
            'bg-ds-yellow-800',
            'bg-ds-yellow-900',
        ],
    },
    {
        name: 'ds-red',
        swatchClasses: [
            'bg-ds-red-50',
            'bg-ds-red-100',
            'bg-ds-red-200',
            'bg-ds-red-300',
            'bg-ds-red-400',
            'bg-ds-red-500',
            'bg-ds-red-600',
            'bg-ds-red-700',
            'bg-ds-red-800',
            'bg-ds-red-900',
        ],
    },
];

const getClassVariant = (cssClass: string) => {
    return cssClass.split('-').pop();
};
</script>
